<script lang="ts" setup>
import { computed, type PropType } from 'vue';
import { useI18n } from 'vue-i18n';
import { useRouter } from 'vue-router';

import { useLogInOut } from '@exchange/libs/auth/ui/src';
import { MarketState } from '@exchange/libs/market/service/src';

export interface TextObjFromLokalise {
  SUSPENDED?: string | { href: string; text: string };
  IN_MAINTENANCE?: string | { href: string; text: string };
  NEW?: string | { href: string; text: string };
  POST_ONLY?: string;
}

const props = defineProps({
  hasBeenAuthenticated: { type: Boolean, required: true },
  marketName: { type: String, required: false },
  marketState: { type: String as PropType<MarketState>, required: false },
});

const { t } = useI18n({ useScope: 'global' });
const router = useRouter();
const { login } = useLogInOut();

const getTextFrom = (property: TextObjFromLokalise, marketState: MarketState | undefined) => {
  if (!marketState) {
    return undefined;
  }

  switch (marketState) {
    case MarketState.SUSPENDED:
      return property.SUSPENDED;
    case MarketState.IN_MAINTENANCE:
      return property.IN_MAINTENANCE;
    case MarketState.NEW:
      return property.NEW;
    case MarketState.POST_ONLY:
      return property.POST_ONLY;
    default:
      return undefined;
  }
};

const titles = computed(() => ({
  SUSPENDED: t('modules.market.suspended.overlay.title'),
  IN_MAINTENANCE: t('modules.market.inMaintenance.overlay.title'),
  NEW: props.marketName ? t('modules.market.new.overlay.title', { market: props.marketName }) : '',
  POST_ONLY: props.marketName ? t('modules.market.postOnly.overlay.title', { market: props.marketName }) : '',
}));
const title = computed(() => getTextFrom(titles.value, props.marketState));

const textWithoutLink = computed(() => {
  const texts = {
    SUSPENDED: t('modules.market.suspended.overlay.text'),
    IN_MAINTENANCE: t('modules.market.inMaintenance.overlay.text'),
    POST_ONLY: t('modules.market.postOnly.overlay.text'),
  };

  return getTextFrom(texts, props.marketState);
});
const showTextWithoutLink = computed(
  () => props.marketState === MarketState.SUSPENDED || props.marketState === MarketState.IN_MAINTENANCE || props.marketState === MarketState.POST_ONLY,
);

const textWithLinkPath = computed(() => {
  const textPaths = {
    NEW: 'modules.market.new.overlay.text',
  };

  return getTextFrom(textPaths, props.marketState);
});

const linkText = computed(() => {
  const linkTexts = {
    NEW: t('modules.market.new.overlay.inlineURLText'),
  };

  return getTextFrom(linkTexts, props.marketState);
});

const onLinkClick = () => {
  const inlineRoutes = {
    NEW: {
      hash: '#Deposit',
    },
  };

  if (props.hasBeenAuthenticated) {
    const route = props.marketState ? inlineRoutes[props.marketState] : undefined;

    if (!route) {
      return undefined;
    }

    return router.push(route);
  }

  return login();
};
</script>

<template>
  <div
    class="market-overlay"
    :data-state="marketState"
  >
    <div class="market-overlay__message">
      <h2>{{ title }}</h2>
      <p v-if="showTextWithoutLink">{{ textWithoutLink }}</p>
      <i18n-t
        v-else
        scope="global"
        tag="p"
        :keypath="textWithLinkPath"
      >
        <template #inlineUrl>
          <x-button
            class="market-overlay__link"
            size="text-like"
            variant="link"
            :text="linkText"
            @click="onLinkClick"
          />
        </template>
      </i18n-t>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.market-overlay {
  position: absolute;
  z-index: var(--market-overlay-z-index);
  top: 0;
  left: 0;
  display: flex;
  width: 100%;
  height: 100%;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  padding: 20px;

  /** native apps: iso does not like z-index */
  background-color: rgb(var(--v-theme-overlay-2));
  text-align: center;
  transform: translate3d(0, 0, 1px);
  transition: opacity 200ms ease-out;

  &__message {
    position: relative;
    top: 30%;
    max-width: 300px;

    h2 {
      font-size: var(--font-size-lg);
      font-weight: var(--font-weight-medium);
    }

    p {
      color: rgb(var(--v-theme-text-primary));
      font-size: var(--font-size-sm);
    }
  }

  &[data-state='POST_ONLY'],
  &[data-state='NEW'] {
    h2 {
      color: rgb(var(--v-theme-primary));
    }
  }

  &[data-state='SUSPENDED'],
  &[data-state='IN_MAINTENANCE'] {
    h2 {
      color: rgb(var(--v-theme-warning));
    }
  }

  &__link {
    color: inherit;
  }
}
</style>
